// TableEditor — the Table block, a NEW Build-Menu Content block.
// ISP-4018 (Fable Hail Mary Wave 5). Deck: concepts/builder-blocks/02-table-block-editor.
//
// Deck feature set (all present): an editable N×M cell grid (deck default 3×6)
// with add / remove row & column; a header-row toggle; per-column alignment
// (numeric columns right-align, per deck); Font (the app's 9 curated faces);
// Font color + Header bar color via the shared ColorField; an optional caption
// (accessible <caption>). Longer tables show 4 rows in the Segment and scroll —
// enforced at the render layer (Table.jsx), stated here as helper copy.
//
// Presentational + controlled: `value` (the table model from wiring.normalize)
// + `onChange(nextValue)`. NO redux / craft dependency — the craft settings
// adapter (Table/TableEditorSettings) feeds it and persists via setProp. All
// grid mutations go through the pure ops in CodeBlocks/Table/wiring.js so the
// editor and the render can never desync on column/row counts.

import React from "react";
import styled from "styled-components";
import BlockEditorPane from "../BlockEditorPane";
import EditorSection from "../EditorSection";
import AdvancedOptions from "../AdvancedOptions";
import { SegChoice, Toggle, ColorField, FontSelect, QuadStepper } from "../controls";
import { fontFamily, field, focusRing } from "../../../../theme/tokens";
import { fieldChrome } from "../EditorField";
import ColorPopup from "../../Shared/ColorPopup";
import wiring from "../../CodeBlocks/Table/wiring";

const INK = fieldChrome.ink;
const MUTED = fieldChrome.muted;
const BORDER = fieldChrome.border;
const PURPLE = fieldChrome.accent;

const TableGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3.5" y="4.5" width="17" height="15" rx="1.8" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.5 9.5h17M3.5 14.5h17M9 4.5v15M15 4.5v15" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const GridMeta = styled.div`
  font-family: ${fontFamily};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: ${MUTED};
  text-transform: uppercase;
  margin-bottom: 6px;
`;
const NewBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 6px;
  background: #ede4fe;
  color: #6c1fb8;
  font-family: ${fontFamily};
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.06em;
  vertical-align: middle;
`;
const Hint = styled.div`
  font-family: ${fontFamily};
  font-size: 11px;
  color: ${MUTED};
  line-height: 1.4;
  margin-top: 6px;
`;

// horizontal scroll so many columns never overflow the pane
const GridScroll = styled.div`
  overflow-x: auto;
  padding-bottom: 4px;
`;
const GridTable = styled.div`
  display: inline-grid;
  gap: 6px;
  grid-template-columns: ${(p) => `repeat(${p.$cols}, minmax(96px, 1fr)) 40px`};
  align-items: center;
`;
const CellInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  min-height: 40px;
  padding: 0 10px;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: ${(p) => (p.$header ? 700 : 400)};
  color: ${INK};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:hover { border-color: ${field.borderHover}; }
  /* ISP-9999 (gordon-0923-P1-REPEAT-kelly-field-focus-style-one-shared-
     border-only-app-wide) — dropped the box-shadow glow, same as the shared
     EditorField.jsx controlChrome this cell mirrors. Border-color recolor
     only, no outline, no box-shadow. */
  &:focus-visible {
    outline: none;
    border-color: ${PURPLE};
    box-shadow: none;
  }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const IconBtn = styled.button`
  width: 40px;
  height: 40px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${BORDER};
  border-radius: 8px;
  background: #fff;
  color: ${MUTED};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  &:hover:not(:disabled) { background: #faf7fe; color: ${PURPLE}; border-color: ${PURPLE}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
`;
const ColHandleRow = styled.div`
  display: inline-grid;
  gap: 6px;
  grid-template-columns: ${(p) => `repeat(${p.$cols}, minmax(96px, 1fr)) 40px`};
  margin-bottom: 6px;
`;
const ColHandle = styled.button`
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid ${BORDER};
  border-radius: 8px 8px 0 0;
  background: #f6f7f9;
  color: ${MUTED};
  font-family: ${fontFamily};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { color: #d92d20; border-color: #f2b8b5; background: #fff5f5; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
`;
const AddRowBtn = styled.button`
  width: 100%;
  min-height: 44px;
  margin-top: 8px;
  border: 1px dashed ${PURPLE};
  border-radius: 8px;
  background: #faf7fe;
  color: ${PURPLE};
  font-family: ${fontFamily};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #f2ecfe; }
  &:focus-visible { outline: none; box-shadow: ${focusRing.shadow}; }
`;
const AlignRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;
const AlignName = styled.span`
  flex: 1;
  min-width: 0;
  font-family: ${fontFamily};
  font-size: 12px;
  font-weight: 600;
  color: ${INK};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CAPTION_MAX = 120;

const TableEditor = ({
  value,
  onChange = () => {},
  margin,
  onMarginChange = () => {},
  segmentName,
  onBack,
  status = "ready",
  onRetry,
  errorMessage,
}) => {
  const v = wiring.normalize(value);
  const cols = v.header.length;
  const emit = (next) => onChange(next);

  const onCellKeyDown = (e) => {
    // Ctrl/Cmd+Enter adds a row from within any cell (keyboard-only authoring).
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      emit(wiring.addRow(v));
    }
  };

  return (
    <BlockEditorPane
      blockName="Table"
      segmentName={segmentName}
      glyph={<TableGlyph />}
      onBack={onBack}
      status={status}
      onRetry={onRetry}
      errorMessage={errorMessage}
    >
      <EditorSection heading={<span>Basic<NewBadge>NEW</NewBadge></span>}>
        <div>
          <GridMeta data-testid="table-dims">{`Table · ${cols} × ${v.rows.length}`}</GridMeta>
          <GridScroll>
            {/* per-column remove handles */}
            <ColHandleRow $cols={cols}>
              {v.header.map((h, c) => (
                <ColHandle
                  key={`h-${c}`}
                  type="button"
                  aria-label={`Remove column ${c + 1}${h ? ` (${h})` : ""}`}
                  disabled={cols <= wiring.limits.MIN_COLS}
                  data-testid={`rm-col-${c}`}
                  onClick={() => emit(wiring.removeColumn(v, c))}
                >
                  ✕ col
                </ColHandle>
              ))}
              <IconBtn
                type="button"
                aria-label="Add column"
                data-testid="add-col"
                disabled={cols >= wiring.limits.MAX_COLS}
                onClick={() => emit(wiring.addColumn(v))}
              >
                +
              </IconBtn>
            </ColHandleRow>

            {/* header row (only when the header is shown) */}
            {v.showHeader && (
              <GridTable $cols={cols} style={{ marginBottom: 6 }}>
                {v.header.map((h, c) => (
                  <CellInput
                    key={`hd-${c}`}
                    $header
                    value={h}
                    aria-label={`Header for column ${c + 1}`}
                    data-testid={`header-${c}`}
                    placeholder={`Column ${c + 1}`}
                    onKeyDown={onCellKeyDown}
                    onChange={(e) => emit(wiring.setHeaderCell(v, c, e.target.value))}
                  />
                ))}
                <span aria-hidden="true" />
              </GridTable>
            )}

            {/* body rows */}
            {v.rows.map((row, r) => (
              <GridTable key={`row-${r}`} $cols={cols}>
                {row.map((cell, c) => (
                  <CellInput
                    key={`c-${r}-${c}`}
                    value={cell}
                    aria-label={`Row ${r + 1}, column ${c + 1}`}
                    data-testid={`cell-${r}-${c}`}
                    onKeyDown={onCellKeyDown}
                    onChange={(e) => emit(wiring.setCell(v, r, c, e.target.value))}
                  />
                ))}
                <IconBtn
                  type="button"
                  aria-label={`Remove row ${r + 1}`}
                  data-testid={`rm-row-${r}`}
                  disabled={v.rows.length <= wiring.limits.MIN_ROWS}
                  onClick={() => emit(wiring.removeRow(v, r))}
                >
                  ✕
                </IconBtn>
              </GridTable>
            ))}
          </GridScroll>

          <AddRowBtn type="button" data-testid="add-row" onClick={() => emit(wiring.addRow(v))}>
            + Add row
          </AddRowBtn>
          <Hint>
            4 rows show in the Segment — longer tables scroll in the panel with the
            header row pinned. Ctrl+Enter adds a row from any cell.
          </Hint>
        </div>

        <Toggle
          label="Header row"
          checked={v.showHeader}
          onChange={(b) => emit(wiring.setShowHeader(v, b))}
        />

        <FontSelect value={v.font} onChange={(f) => emit(wiring.setField(v, "font", f))} />

        <ColorField
          label="Font color"
          color={v.fontColor}
          onChange={(c) => emit(wiring.setField(v, "fontColor", c))}
          onReset={() => emit(wiring.setField(v, "fontColor", wiring.DEFAULT.fontColor))}
        />

        <ColorPopup
          title="Header Color"
          background={v.headerColor}
          handleChangeBackgroundColor={(c) => emit(wiring.setField(v, "headerColor", c))}
          resetBackgroundColor={() => emit(wiring.setField(v, "headerColor", wiring.DEFAULT.headerColor))}
          showSlider={false}
        />
      </EditorSection>

      <AdvancedOptions>
        <EditorSection heading="Accessibility" dense>
          <div>
            <label
              htmlFor="table-caption-input"
              style={{
                display: "block", fontFamily, fontSize: 12, fontWeight: 600,
                color: INK, marginBottom: 4,
              }}
            >
              Table caption
            </label>
            <CellInput
              id="table-caption-input"
              value={v.caption}
              maxLength={CAPTION_MAX}
              placeholder="Describe this table for screen readers…"
              data-testid="caption"
              onChange={(e) => emit(wiring.setField(v, "caption", e.target.value))}
            />
            <Hint>Read by screen readers and shown above the table when set.</Hint>
          </div>
        </EditorSection>

        <EditorSection heading="Spacing" dense>
          {/* fix-gordon-0930-P1-block-spacing-controls-every-block-addendum-1458:
              a Table has no internal padding of its own (TableView.jsx renders the
              <table> flush inside its scroll wrapper) — Left/Right margin has no
              visible effect and only risks the table clipping against the segment
              edge, so this reuses QuadStepper's existing fullWidth prop to hide
              those two inputs (Gordon ruling: hideHorizontal for Table and Graph
              only). Top/Bottom stay editable — that's the spacing that visibly
              separates stacked blocks (see Table.jsx's default bottom margin). */}
          <QuadStepper
            label="Margin"
            value={margin}
            onChange={onMarginChange}
            fullWidth
          />
        </EditorSection>

        <EditorSection heading="Column alignment" dense>
          {v.header.map((h, c) => (
            <AlignRow key={`al-${c}`}>
              <AlignName title={h || `Column ${c + 1}`}>{h || `Column ${c + 1}`}</AlignName>
              <SegChoice
                value={v.aligns[c]}
                options={[
                  { label: "Left", value: "left" },
                  { label: "Center", value: "center" },
                  { label: "Right", value: "right" },
                ]}
                onChange={(a) => emit(wiring.setColumnAlign(v, c, a))}
              />
            </AlignRow>
          ))}
        </EditorSection>
      </AdvancedOptions>
    </BlockEditorPane>
  );
};

export default TableEditor;
